/**
 * Cat Instinct, drawn.
 *
 * The renderer-facing half of src/systems/instinct.ts, which decides WHAT gets
 * marked. This file only decides how it looks.
 *
 * One InstancedMesh per category, so the whole mode costs five draw calls
 * whether there is one mark on screen or eighty. Per-object marker meshes would
 * scale with level content and are not an option at any budget.
 */

import * as THREE from 'three';
import { tuning } from '@/core/tuning';
import {
  ignoresRange,
  INSTINCT_CATEGORIES,
  instinctPulse,
  type InstinctCategory,
  type InstinctMark,
} from '@/systems/instinct';
import { setSharedDesaturation } from '@/render/toonMaterial';

/** Per category. Level 1 has 16 props; this leaves room for the dense levels. */
const CAPACITY = 128;

export class InstinctView {
  private readonly meshes: Record<InstinctCategory, THREE.InstancedMesh>;
  private readonly geometry: THREE.BufferGeometry;
  private readonly materials: THREE.MeshBasicMaterial[] = [];
  private readonly scratch = new THREE.Matrix4();
  private readonly position = new THREE.Vector3();
  private readonly quaternion = new THREE.Quaternion();
  private readonly scale = new THREE.Vector3();
  private active = false;
  private elapsed = 0;
  private counts: Record<InstinctCategory, number> = {
    prop: 0,
    key: 0,
    trace: 0,
    hideSpot: 0,
    goal: 0,
  };

  constructor(private readonly scene: THREE.Scene) {
    // An octahedron reads as a "glint" from any angle and is 8 triangles.
    this.geometry = new THREE.OctahedronGeometry(1, 0);

    const meshes = {} as Record<InstinctCategory, THREE.InstancedMesh>;
    for (const category of INSTINCT_CATEGORIES) {
      const material = new THREE.MeshBasicMaterial({
        color: new THREE.Color(tuning.instinct.markerColors[category]),
        transparent: true,
        opacity: tuning.instinct.markerOpacity,
        // Keys, traces, hide spots and goals answer "where do I go next", which a
        // wall must not be allowed to veto. Prop marks stay depth-tested: they
        // answer "what can I touch right now", which is about the room you are in.
        depthTest: !ignoresRange(category),
      });
      this.materials.push(material);

      const mesh = new THREE.InstancedMesh(this.geometry, material, CAPACITY);
      mesh.name = `instinct:${category}`;
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      mesh.frustumCulled = false;
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      mesh.renderOrder = ignoresRange(category) ? 10 : 0;
      mesh.visible = false;
      mesh.count = 0;
      scene.add(mesh);
      meshes[category] = mesh;
    }
    this.meshes = meshes;
  }

  get isActive(): boolean {
    return this.active;
  }

  /** Read-only, for the debug HUD and the Playwright assertions. */
  get markCounts(): Readonly<Record<InstinctCategory, number>> {
    return this.counts;
  }

  /**
   * Hold or release. Immediate in both directions - no fade, no cooldown, no
   * transition to wait through. See DESIGN.md §8.
   */
  setActive(active: boolean): void {
    if (this.active === active) return;
    this.active = active;
    setSharedDesaturation(active ? tuning.instinct.desaturation : 0);
    if (!active) {
      this.elapsed = 0;
      for (const category of INSTINCT_CATEGORIES) {
        this.meshes[category].visible = false;
        this.meshes[category].count = 0;
        this.counts[category] = 0;
      }
    }
  }

  /**
   * `realDt` is wall-clock, not simulation time: the markers must keep pulsing at
   * the same rate while the world around them is in slow motion.
   */
  update(marks: readonly InstinctMark[], realDt: number): void {
    if (!this.active) return;
    this.elapsed += realDt;

    const pulse = instinctPulse(this.elapsed);
    const size = tuning.instinct.markerSize * (1 + pulse * tuning.instinct.markerPulseScale);
    this.quaternion.setFromAxisAngle(UP, this.elapsed);

    const written: Record<InstinctCategory, number> = {
      prop: 0,
      key: 0,
      trace: 0,
      hideSpot: 0,
      goal: 0,
    };
    for (const mark of marks) {
      const index = written[mark.category];
      if (index >= CAPACITY) continue;
      this.scale.setScalar(size * mark.scale);
      this.position.set(
        mark.position.x,
        mark.position.y + tuning.instinct.markerLift,
        mark.position.z,
      );
      this.scratch.compose(this.position, this.quaternion, this.scale);
      this.meshes[mark.category].setMatrixAt(index, this.scratch);
      written[mark.category] = index + 1;
    }

    for (const category of INSTINCT_CATEGORIES) {
      const mesh = this.meshes[category];
      mesh.count = written[category];
      mesh.visible = written[category] > 0;
      mesh.instanceMatrix.needsUpdate = true;
      this.counts[category] = written[category];
    }
  }

  dispose(): void {
    setSharedDesaturation(0);
    for (const category of INSTINCT_CATEGORIES) {
      this.scene.remove(this.meshes[category]);
      this.meshes[category].dispose();
    }
    for (const material of this.materials) material.dispose();
    this.geometry.dispose();
  }
}

const UP = new THREE.Vector3(0, 1, 0);
